import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon, Save, Calendar, Users, MapPin, Clock,
  ChevronLeft, ChevronRight, RefreshCw, Globe, Wifi, Shield, RotateCcw,
  Eye, Plus, Edit, Trash2, FolderOpen, ClipboardList, Heart, Microscope,
  Sprout, Palette, FileText, CheckSquare
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module definitions for permission settings
const MODULES = [
  { key: 'projects', label: 'Projects', icon: FolderOpen, color: '#7077FE' },
  { key: 'subprojects', label: 'Sub-Projects', icon: FolderOpen, color: '#6366F1' },
  { key: 'tasks', label: 'Tasks (CNESS)', icon: ClipboardList, color: '#3B82F6' },
  { key: 'self_care', label: 'Self Care', icon: Heart, color: '#EC4899' },
  { key: 'rnd', label: 'R&D', icon: Microscope, color: '#8B5CF6' },
  { key: 'personal_growth', label: 'Personal Growth', icon: Sprout, color: '#10B981' },
  { key: 'creative', label: 'Creative', icon: Palette, color: '#F59E0B' },
  { key: 'sops', label: 'SOPs', icon: FileText, color: '#6B7280' },
  { key: 'daily_scan', label: 'Daily Scan', icon: CheckSquare, color: '#14B8A6' }
];

const ROLES = [
  { key: 'superadmin', label: 'Super Admin', color: '#EF4444' },
  { key: 'ceo', label: 'CEO', color: '#7077FE' },
  { key: 'manager', label: 'Manager', color: '#10B981' },
  { key: 'team_lead', label: 'Team Lead', color: '#F59E0B' },
  { key: 'team_member', label: 'Team Member', color: '#3B82F6' },
  { key: 'educator', label: 'Educator', color: '#8B5CF6' }
];

const TeamSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('attendance-log');
  const [leaveSettings, setLeaveSettings] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('manager');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const isAdmin = ['superadmin', 'ceo'].includes(user?.role);

  const fetchRolePermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/settings/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolePermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch role permissions:', error);
    }
  }, []);

  const fetchLeaveSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/settings/leave`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch leave settings:', error);
    }
  }, []);

  const fetchLeaveBalances = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/leave-balance/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balances:', error);
    }
  }, []);

  const fetchAttendanceLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/attendance/team?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance logs:', error);
    }
  }, [selectedDate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeaveSettings(), fetchLeaveBalances(), fetchAttendanceLogs(), fetchRolePermissions()]);
      setLoading(false);
    };
    if (isAdmin) {
      loadData();
    }
  }, [fetchLeaveSettings, fetchLeaveBalances, fetchAttendanceLogs, fetchRolePermissions, isAdmin]);

  useEffect(() => {
    if (isAdmin && activeTab === 'attendance-log') {
      fetchAttendanceLogs();
    }
  }, [selectedDate, activeTab, isAdmin, fetchAttendanceLogs]);

  const handleSaveLeaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/settings/leave`, leaveSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leave settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (role, module, permission, value) => {
    setRolePermissions(prev => {
      return prev.map(rp => {
        if (rp.role === role) {
          return {
            ...rp,
            [module]: {
              ...rp[module],
              [permission]: value
            }
          };
        }
        return rp;
      });
    });
  };

  const handleSaveRolePermissions = async (role) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const rolePerm = rolePermissions.find(rp => rp.role === role);
      if (!rolePerm) return;

      const permissionsToSave = {};
      MODULES.forEach(module => {
        permissionsToSave[module.key] = rolePerm[module.key];
      });

      await axios.put(`${API_URL}/settings/permissions/${role}`, permissionsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${ROLES.find(r => r.key === role)?.label || role} permissions saved successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAllPermissions = async () => {
    if (!window.confirm('Are you sure you want to reset all role permissions to defaults? This cannot be undone.')) {
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/permissions/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All permissions reset to defaults');
      fetchRolePermissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: { bg: '#D1FAE5', color: '#059669', label: 'Present' },
      absent: { bg: '#FEE2E2', color: '#DC2626', label: 'Absent' },
      on_leave: { bg: '#DBEAFE', color: '#2563EB', label: 'On Leave' },
      half_day: { bg: '#FEF3C7', color: '#D97706', label: 'Half Day' }
    };
    const style = styles[status] || styles.absent;
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600',
        background: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  if (!isAdmin) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <SettingsIcon size={48} style={{ marginBottom: '16px', opacity: 0.5, color: 'var(--text-tertiary)' }} />
        <h2 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-secondary)' }}>Only administrators can access team settings.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="content-area" data-testid="team-settings-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SettingsIcon size={32} color="#7077FE" />
            Team Settings
          </h1>
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Configure attendance and leave policies</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveTab('attendance-log')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'attendance-log' ? '#7077FE' : 'transparent',
            color: activeTab === 'attendance-log' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-attendance-log"
        >
          <MapPin size={18} />
          Attendance Log
        </button>
        <button
          onClick={() => setActiveTab('leave-settings')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'leave-settings' ? '#7077FE' : 'transparent',
            color: activeTab === 'leave-settings' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-leave-settings"
        >
          <Calendar size={18} />
          Leave Settings
        </button>
        <button
          onClick={() => setActiveTab('leave-balances')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'leave-balances' ? '#7077FE' : 'transparent',
            color: activeTab === 'leave-balances' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-leave-balances"
        >
          <Users size={18} />
          Leave Balances
        </button>
        <button
          onClick={() => setActiveTab('role-permissions')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'role-permissions' ? '#7077FE' : 'transparent',
            color: activeTab === 'role-permissions' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-role-permissions"
        >
          <Shield size={18} />
          Role Permissions
        </button>
      </div>

      {/* Attendance Log Tab */}
      {activeTab === 'attendance-log' && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Attendance Log with Location
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => navigateDate(-1)}
                style={{
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              />
              <button
                onClick={() => navigateDate(1)}
                style={{
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
              >
                <ChevronRight size={18} />
              </button>
              <button
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                style={{
                  padding: '8px 16px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  fontSize: '13px'
                }}
              >
                Today
              </button>
              <button
                onClick={fetchAttendanceLogs}
                style={{
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>

          <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>
            Showing attendance for: <strong>{formatDate(selectedDate)}</strong>
          </p>

          {attendanceLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No attendance records for this date</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>Clock In</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Globe size={14} /> In Location
                      </div>
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Wifi size={14} /> In IP
                      </div>
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>Clock Out</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Globe size={14} /> Out Location
                      </div>
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Wifi size={14} /> Out IP
                      </div>
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceLogs.map((record, idx) => (
                    <tr key={record.user_id || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{record.user_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{record.user_email}</div>
                      </td>
                      <td style={{ padding: '14px 12px' }}>{getStatusBadge(record.status)}</td>
                      <td style={{ padding: '14px 12px', color: '#10B981', fontWeight: '500' }}>{formatTime(record.clock_in)}</td>
                      <td style={{ padding: '14px 12px' }}>
                        {record.clock_in_city && record.clock_in_country ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <MapPin size={14} color="#7077FE" />
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                              {record.clock_in_city}, {record.clock_in_country}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        {record.clock_in_ip ? (
                          <code style={{ fontSize: '12px', padding: '4px 8px', background: 'var(--bg-secondary)', borderRadius: '4px', color: 'var(--text-secondary)' }}>
                            {record.clock_in_ip}
                          </code>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px', color: '#EF4444', fontWeight: '500' }}>{formatTime(record.clock_out)}</td>
                      <td style={{ padding: '14px 12px' }}>
                        {record.clock_out_city && record.clock_out_country ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <MapPin size={14} color="#F59E0B" />
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                              {record.clock_out_city}, {record.clock_out_country}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        {record.clock_out_ip ? (
                          <code style={{ fontSize: '12px', padding: '4px 8px', background: 'var(--bg-secondary)', borderRadius: '4px', color: 'var(--text-secondary)' }}>
                            {record.clock_out_ip}
                          </code>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px', color: '#7077FE', fontWeight: '600' }}>
                        {record.total_hours ? `${record.total_hours}h` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Leave Settings Tab */}
      {activeTab === 'leave-settings' && leaveSettings && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Leave Policy Settings
            </h3>
            <button
              onClick={handleSaveLeaveSettings}
              disabled={saving}
              className="gradient-btn"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
              data-testid="save-leave-settings-btn"
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            {/* Casual Leave */}
            <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#3B82F6', fontSize: '14px' }}>
                Casual Leave (per year)
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={leaveSettings.casual_leave_count}
                onChange={(e) => setLeaveSettings({ ...leaveSettings, casual_leave_count: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                data-testid="casual-leave-input"
              />
            </div>

            {/* Sick Leave */}
            <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#EF4444', fontSize: '14px' }}>
                Sick Leave (per year)
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={leaveSettings.sick_leave_count}
                onChange={(e) => setLeaveSettings({ ...leaveSettings, sick_leave_count: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                data-testid="sick-leave-input"
              />
            </div>

            {/* Annual Leave */}
            <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#10B981', fontSize: '14px' }}>
                Annual Leave (per year)
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={leaveSettings.annual_leave_count}
                onChange={(e) => setLeaveSettings({ ...leaveSettings, annual_leave_count: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                data-testid="annual-leave-input"
              />
            </div>

            {/* Personal Leave */}
            <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#F59E0B', fontSize: '14px' }}>
                Personal Leave (per year)
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={leaveSettings.personal_leave_count}
                onChange={(e) => setLeaveSettings({ ...leaveSettings, personal_leave_count: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                data-testid="personal-leave-input"
              />
            </div>
          </div>

          {/* Additional Settings */}
          <div style={{ marginTop: '24px', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Additional Settings
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={leaveSettings.unpaid_leave_allowed}
                    onChange={(e) => setLeaveSettings({ ...leaveSettings, unpaid_leave_allowed: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ color: 'var(--text-primary)', fontSize: '14px' }}>Allow Unpaid Leave</span>
                </label>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={leaveSettings.carry_forward_allowed}
                    onChange={(e) => setLeaveSettings({ ...leaveSettings, carry_forward_allowed: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ color: 'var(--text-primary)', fontSize: '14px' }}>Allow Carry Forward</span>
                </label>
              </div>
              {leaveSettings.carry_forward_allowed && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    Max Carry Forward Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={leaveSettings.max_carry_forward_days}
                    onChange={(e) => setLeaveSettings({ ...leaveSettings, max_carry_forward_days: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  Leave Year Start Month
                </label>
                <select
                  value={leaveSettings.leave_year_start_month}
                  onChange={(e) => setLeaveSettings({ ...leaveSettings, leave_year_start_month: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                >
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                    <option key={idx + 1} value={idx + 1}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Balances Tab */}
      {activeTab === 'leave-balances' && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Employee Leave Balances ({new Date().getFullYear()})
            </h3>
            <button
              onClick={fetchLeaveBalances}
              style={{
                padding: '8px 16px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '500',
                fontSize: '13px'
              }}
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>

          {leaveBalances.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No employee data found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>Employee</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#3B82F6', fontSize: '12px', fontWeight: '600' }}>Casual</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#EF4444', fontSize: '12px', fontWeight: '600' }}>Sick</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#10B981', fontSize: '12px', fontWeight: '600' }}>Annual</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#F59E0B', fontSize: '12px', fontWeight: '600' }}>Personal</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#6B7280', fontSize: '12px', fontWeight: '600' }}>Unpaid Taken</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map((balance, idx) => (
                    <tr key={balance.user_id || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{balance.user_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{balance.role}</div>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{ color: '#3B82F6', fontWeight: '600' }}>{balance.casual_remaining}</span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}> / {balance.casual_used + balance.casual_remaining}</span>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{ color: '#EF4444', fontWeight: '600' }}>{balance.sick_remaining}</span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}> / {balance.sick_used + balance.sick_remaining}</span>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{ color: '#10B981', fontWeight: '600' }}>{balance.annual_remaining}</span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}> / {balance.annual_used + balance.annual_remaining}</span>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{ color: '#F59E0B', fontWeight: '600' }}>{balance.personal_remaining}</span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}> / {balance.personal_used + balance.personal_remaining}</span>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{ color: '#6B7280', fontWeight: '600' }}>{balance.unpaid_taken}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Role Permissions Tab */}
      {activeTab === 'role-permissions' && (
        <div className="card" style={{ padding: '24px' }} data-testid="role-permissions-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                Role-Based Permissions
              </h3>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                Configure what each role can do in each module
              </p>
            </div>
            <button
              onClick={handleResetAllPermissions}
              disabled={saving}
              style={{
                padding: '10px 16px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
                fontSize: '13px'
              }}
              data-testid="reset-all-permissions-btn"
            >
              <RotateCcw size={16} />
              Reset All to Defaults
            </button>
          </div>

          {/* Role Selection Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {ROLES.filter(r => r.key !== 'superadmin').map(role => (
              <button
                key={role.key}
                onClick={() => setSelectedRole(role.key)}
                style={{
                  padding: '8px 16px',
                  background: selectedRole === role.key ? role.color : 'var(--bg-secondary)',
                  color: selectedRole === role.key ? 'white' : 'var(--text-secondary)',
                  border: `1px solid ${selectedRole === role.key ? role.color : 'var(--border-color)'}`,
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
                data-testid={`role-tab-${role.key}`}
              >
                {role.label}
              </button>
            ))}
          </div>

          {/* Permission Grid for Selected Role */}
          {rolePermissions.length > 0 && (
            <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: ROLES.find(r => r.key === selectedRole)?.color || 'var(--text-primary)' }}>
                  {ROLES.find(r => r.key === selectedRole)?.label} Permissions
                </h4>
                <button
                  onClick={() => handleSaveRolePermissions(selectedRole)}
                  disabled={saving}
                  className="gradient-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
                  data-testid={`save-permissions-${selectedRole}`}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Permission Matrix */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', width: '200px' }}>Module</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <Eye size={14} /> View
                        </div>
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <Plus size={14} /> Create
                        </div>
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <Edit size={14} /> Edit
                        </div>
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <Trash2 size={14} /> Delete
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map(module => {
                      const rolePerms = rolePermissions.find(rp => rp.role === selectedRole);
                      const modulePerms = rolePerms?.[module.key] || { view: false, create: false, edit: false, delete: false };
                      const ModuleIcon = module.icon;
                      
                      return (
                        <tr key={module.key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '14px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '8px', 
                                background: `${module.color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <ModuleIcon size={16} color={module.color} />
                              </div>
                              <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{module.label}</span>
                            </div>
                          </td>
                          {['view', 'create', 'edit', 'delete'].map(permission => (
                            <td key={permission} style={{ padding: '14px 12px', textAlign: 'center' }}>
                              <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={modulePerms[permission] || false}
                                  onChange={(e) => handlePermissionChange(selectedRole, module.key, permission, e.target.checked)}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                    accentColor: module.color
                                  }}
                                  data-testid={`perm-${selectedRole}-${module.key}-${permission}`}
                                />
                              </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div style={{ marginTop: '20px', padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <h5 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>Permission Legend</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  <div><strong>View</strong>: Can see items</div>
                  <div><strong>Create</strong>: Can add new items</div>
                  <div><strong>Edit</strong>: Can modify existing items</div>
                  <div><strong>Delete</strong>: Can remove items</div>
                </div>
              </div>
            </div>
          )}

          {rolePermissions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Shield size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No permission data available. Click "Reset All to Defaults" to initialize.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
